import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { FinanceOverview, Membership, Person } from '../../shared/api/types';
import { Alert, Button, Field } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { parseMoney } from './money';

type Kind = 'income' | 'expense' | 'transfer';
const KINDS: Record<Kind, string> = { income: 'Ingreso', expense: 'Gasto', transfer: 'Transferencia' };

/** Registrar un ingreso, un gasto o una transferencia. El servidor valida fecha, cuenta y categoría. */
export function MovementForm({ churchId, overview, onSaved }: { churchId: string; overview: FinanceOverview; onSaved: (message: string) => void }) {
  const me = useMe();
  const [kind, setKind] = useState<Kind>('income');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const accounts = overview.accounts.filter((a) => !a.archived);
  const categories = overview.categories.filter((c) => c.kind === kind);
  const members = useAsync(
    (signal) =>
      me.can('members.read')
        ? api.get<{ items: (Membership & { person: Person | null })[] }>(`/multichurch/churches/${churchId}/memberships?status=active`, signal).then((r) => r.items)
        : Promise.resolve([]),
    [churchId],
  );

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const amountCents = parseMoney(String(form.get('amount') ?? ''));
    if (amountCents === null) {
      setError('Escribe un monto válido, por ejemplo 1,250.50.');
      return;
    }
    const text = (name: string) => String(form.get(name) ?? '').trim() || null;
    setBusy(true);
    setError(null);
    try {
      if (kind === 'transfer') {
        await api.post(`/finance/churches/${churchId}/transfers`, {
          fromAccountId: text('fromAccountId'),
          toAccountId: text('toAccountId'),
          amountCents,
          occurredOn: text('occurredOn'),
          description: text('description'),
        });
      } else {
        const created = await api.post<{ id: string }>(`/finance/churches/${churchId}/movements`, {
          accountId: text('accountId'),
          kind,
          amountCents,
          occurredOn: text('occurredOn'),
          categoryId: text('categoryId'),
          description: text('description'),
          personId: text('personId'),
        });
        // Comprobantes: se suben después de registrar; si alguno falla, el movimiento ya quedó.
        const failed: string[] = [];
        for (const f of files) {
          await api.upload(`/finance/churches/${churchId}/movements/${created.id}/files`, f).catch(() => failed.push(f.name));
        }
        if (failed.length) {
          formEl.reset();
          setFiles([]);
          onSaved(`${KINDS[kind]} registrado, pero no se pudo subir: ${failed.join(', ')}. Adjúntalo desde Movimientos (PDF o imagen).`);
          return;
        }
      }
      formEl.reset();
      setFiles([]);
      onSaved(`${KINDS[kind]} registrado${kind !== 'transfer' && files.length ? ` con ${files.length} ${files.length === 1 ? 'comprobante' : 'comprobantes'}` : ''}.`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const accountSelect = (name: string, label: string) => (
    <div className="field">
      <label htmlFor={`f-${name}`}>{label}</label>
      <select id={`f-${name}`} name={name} required>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      <div className="tabs" role="tablist" aria-label="Tipo de movimiento">
        {(Object.keys(KINDS) as Kind[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={kind === k} className={`tabs__tab${kind === k ? ' tabs__tab--active' : ''}`} onClick={() => setKind(k)}>
            {KINDS[k]}
          </button>
        ))}
      </div>
      {kind === 'transfer' ? (
        <>
          {accountSelect('fromAccountId', 'Desde la cuenta')}
          {accountSelect('toAccountId', 'Hacia la cuenta')}
        </>
      ) : (
        <>
          {accountSelect('accountId', 'Cuenta')}
          <div className="field">
            <label htmlFor="f-category">Categoría</label>
            <select id="f-category" name="categoryId" required key={kind}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      <Field label="Monto" name="amount" inputMode="decimal" placeholder="0.00" required autoComplete="off" />
      <Field label="Fecha" name="occurredOn" type="date" defaultValue={overview.today} max={overview.today} min={overview.closedThrough ?? undefined} required />
      {kind === 'income' && members.status === 'ready' && members.data.length > 0 ? (
        <div className="field">
          <label htmlFor="f-person">Quién aporta (opcional)</label>
          <select id="f-person" name="personId" defaultValue="">
            <option value="">Sin nombre</option>
            {members.data.map((m) => (
              <option key={m.person_id} value={m.person_id}>
                {m.person ? `${m.person.givenName} ${m.person.familyName}` : m.person_id}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <Field label="Descripción (opcional)" name="description" maxLength={300} />
      {kind !== 'transfer' ? (
        <div className="field">
          <label htmlFor="f-files">Comprobantes (opcional)</label>
          <input id="f-files" type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp,image/gif" onChange={(e) => setFiles([...(e.target.files ?? [])])} />
          <small className="field__hint">PDF o imagen (JPG, PNG): factura, recibo, ticket o foto del sobre.</small>
        </div>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={busy}>
          Registrar {KINDS[kind].toLowerCase()}
        </Button>
      </div>
    </form>
  );
}
